import { KeyRound } from 'lucide-react';
import {
  useEffect,
  useRef,
  useState,
  type FormEvent,
  type ReactNode,
} from 'react';
import { Logo } from '@/components/brand/Logo';
import { Button, Input } from '@/components/ui';
import { isDisplayAccount } from '@/features/auth/display-account';
import { useMe, useSession } from '@/features/auth/session-context';
import { isApiError } from '@/lib/api';
import styles from './Shell.module.css';

const ACTIVITY = [
  'mousemove',
  'mousedown',
  'keydown',
  'wheel',
  'touchstart',
  'scroll',
] as const;

/**
 * Idle lock (SEC-06, #6): after the idle time without use the session is
 * locked on the server and the page content is removed from the screen until
 * the PIN is entered. "Unlock with PIN" only (face/passkey deferred).
 * RL-1666: a waiting-room display account (permissions exactly
 * waiting_room:read) never starts the timer; every other account keeps it,
 * also on the waiting-room page. A session locked elsewhere still shows the
 * lock.
 */
export function IdleLock({ children }: { children: ReactNode }) {
  const me = useMe();
  const { lock } = useSession();
  const last = useRef(0);
  // RL-1666 (D7, review): only a display account (exactly
  // waiting_room:read: the waiting-room TV, numbers only) skips the timer.
  const exempt = isDisplayAccount(me.permissions);

  useEffect(() => {
    if (me.locked || exempt) return;
    last.current = Date.now();
    const bump = () => {
      last.current = Date.now();
    };
    for (const e of ACTIVITY)
      window.addEventListener(e, bump, { passive: true });
    const timer = setInterval(() => {
      if (Date.now() - last.current >= me.idleLockMinutes * 60_000) {
        clearInterval(timer); // lock once; the effect restarts after unlock
        void lock();
      }
    }, 15_000);
    return () => {
      for (const e of ACTIVITY) window.removeEventListener(e, bump);
      clearInterval(timer);
    };
  }, [me.locked, me.idleLockMinutes, lock, exempt]);

  if (me.locked) return <LockScreen />;
  return <>{children}</>;
}

function LockScreen() {
  const me = useMe();
  const { unlock, signOut } = useSession();
  const [pin, setPin] = useState('');
  const [error, setError] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);

  async function onSubmit(e: FormEvent) {
    e.preventDefault();
    setBusy(true);
    setError(null);
    try {
      await unlock(pin);
    } catch (err) {
      setError(isApiError(err) ? err.message : 'Could not unlock.');
      setPin('');
    } finally {
      setBusy(false);
    }
  }

  return (
    <div
      className={styles.lock}
      role="dialog"
      aria-modal="true"
      aria-label="Locked"
    >
      <form className={styles.lockCard} onSubmit={onSubmit}>
        <Logo className={styles.lockLogo} />
        <KeyRound size={22} aria-hidden="true" />
        <h2>Locked</h2>
        <p>{me.fullName} · patient data is hidden</p>
        {me.pinSet ? (
          <>
            <Input
              aria-label="PIN"
              type="password"
              inputMode="numeric"
              autoComplete="off"
              autoFocus
              maxLength={8}
              value={pin}
              onChange={(e) => setPin(e.target.value.replace(/\D/g, ''))}
            />
            {error && (
              <p className={styles.lockError} role="alert">
                {error}
              </p>
            )}
            <Button
              type="submit"
              variant="primary"
              loading={busy}
              disabled={pin.length < 4}
            >
              Unlock with PIN
            </Button>
          </>
        ) : (
          <p className={styles.lockError} role="status">
            No unlock PIN is set for this account. Sign in again, then set a PIN
            from your name in the sidebar.
          </p>
        )}
        <button
          type="button"
          className={styles.lockSignOut}
          onClick={() => void signOut()}
        >
          Sign out
        </button>
      </form>
    </div>
  );
}
